import { useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/catalyst/button";
import { Textarea } from "@/components/catalyst/textarea";
import { PencilIcon } from "@heroicons/react/20/solid";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateWorkOrderMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import { useUnsavedChangesBlocker } from "@/lib/Hooks/useUnsavedChangesBlocker";

export function WorkOrderAdminNotes({
  workOrderId,
  adminNotes,
}: {
  workOrderId: string;
  adminNotes: string | null | undefined;
}) {
  const t = useTranslations("workOrder.details");
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(adminNotes ?? "");
  const { mutateAsync, isPending } = useGqlMutation(updateWorkOrderMutation);

  useUnsavedChangesBlocker(isEditing && notes !== (adminNotes ?? ""));

  function handleSave() {
    mutateAsync({
      id: workOrderId,
      input: {
        admin_notes: notes,
      },
    }).then(() => {
      setIsEditing(false);
    });
  }

  function handleCancel() {
    setNotes(adminNotes ?? "");
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <div className="space-y-3">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.metaKey) {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder={t("addAdminNotes")}
          rows={3}
          className="w-full"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button plain onClick={handleCancel} disabled={isPending}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} isLoading={isPending}>
            {t("save")}
          </Button>
        </div>
      </div>
    );
  }

  if (adminNotes) {
    return (
      <div className="relative">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2">
          <p className="whitespace-pre-wrap text-sm text-gray-700">
            {adminNotes}
          </p>
        </div>
        <Button
          plain
          onClick={() => setIsEditing(true)}
          className="absolute right-0 top-0"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-600"
    >
      <DocumentTextIcon className="h-5 w-5" />
      {t("addAdminNotes")}
    </button>
  );
}
